import React, { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'
import type { Mermaid } from 'mermaid'

export type DiagramBlock = {
  type: 'diagram'
  format: 'mermaid'
  title?: string
  source: string
}

// ---------------------------------------------------------------------------
// Source check — mirrors mermaidSourceProblem in the API's show_report tool
// (graphql-api coach-agent/tools/write/report-tools.ts). Saved messages and
// stream lines are not trusted, so the same rules run again before drawing.
// ---------------------------------------------------------------------------

export const MAX_DIAGRAM_SOURCE_LENGTH = 4000
const DIAGRAM_KINDS = ['flowchart', 'graph', 'timeline', 'gantt', 'mindmap', 'sequenceDiagram']
const HTML_TAG = /<\s*\/?\s*[a-z!][^>]*>/i
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/
// A statement starts a line or follows a `;`.
const CLICK_STATEMENT = /(?:^|;)\s*click\s/im
// The diagram kinds that have click statements
const CLICK_KINDS = ['flowchart', 'graph', 'gantt']
// Sequence actor links and data; `properties` can set an icon image that loads while drawing.
// Only sequence diagrams have them: elsewhere "Link building : 2w" is an ordinary task.
const ACTOR_DATA_STATEMENT = /(?:^|;)\s*(?:links?|properties|details)\s+[^:\n;]+:/im

/** True when the source is one of the allowed diagram kinds and cannot link, run script or restyle. */
export const isSafeMermaidSource = (source: unknown): source is string => {
  if (typeof source !== 'string' || !source.trim()) return false
  if (source.length > MAX_DIAGRAM_SOURCE_LENGTH || CONTROL_CHARS.test(source)) return false
  const firstLine =
    source
      .split(/\r?\n/)
      .find((line) => line.trim())
      ?.trim() ?? ''
  const kind = DIAGRAM_KINDS.find((name) => new RegExp(`^${name}(\\s|$)`).test(firstLine))
  if (!kind) return false

  const lower = source.toLowerCase()
  const compact = lower.replace(/\s+/g, '')
  return !(
    compact.includes('%%{') ||
    (CLICK_KINDS.includes(kind) && CLICK_STATEMENT.test(source)) ||
    (kind === 'sequenceDiagram' && ACTOR_DATA_STATEMENT.test(source)) ||
    /\bhref\b/.test(lower) ||
    // Diagrams never need a web address, and one can be loaded while the diagram is drawn
    compact.includes('://') ||
    /(javascript|vbscript):/.test(compact) ||
    compact.includes('url(') ||
    compact.includes('@import') ||
    compact.includes('@{') ||
    HTML_TAG.test(source)
  )
}

// ---------------------------------------------------------------------------
// Mermaid: loaded on first use (its own chunk), configured once
// ---------------------------------------------------------------------------

let mermaidLoading: Promise<Mermaid> | null = null

const loadMermaid = (): Promise<Mermaid> => {
  if (!mermaidLoading) {
    const loading = import(/* webpackChunkName: "mermaid" */ 'mermaid').then(
      ({ default: mermaid }) => {
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          htmlLabels: false,
          flowchart: { htmlLabels: false },
          theme: 'default',
          // Throw on bad syntax instead of drawing mermaid's error picture into the page
          suppressErrorRendering: true,
        })
        return mermaid
      }
    )
    // A failed chunk download (e.g. after a deploy) can be retried by the next diagram
    loading.catch(() => {
      if (mermaidLoading === loading) mermaidLoading = null
    })
    mermaidLoading = loading
  }
  return mermaidLoading
}

let renderCount = 0

/** Strict mode already sanitizes labels; this second pass keeps only plain SVG drawing tags. */
const sanitizeSvg = (svg: string): string =>
  DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ['a', 'image'],
  }) as string

/**
 * The drawing's labels in order, each once (e.g. a sequence diagram repeats its actors): the text
 * equivalent of the picture for screen readers. Read from an inert document (loads nothing).
 */
const labelsOf = (svg: string): string[] => {
  const doc = new DOMParser().parseFromString(svg, 'text/html')
  const labels = Array.from(doc.querySelectorAll('text'))
    .map((node) => (node.textContent ?? '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
  return Array.from(new Set(labels))
}

const drawDiagram = async (source: string): Promise<{ svg: string; labels: string[] }> => {
  const mermaid = await loadMermaid()
  renderCount += 1
  const { svg } = await mermaid.render(`coach-diagram-${renderCount}`, source)
  const clean = sanitizeSvg(svg)
  if (!clean.trim()) throw new Error('Diagram produced no drawing')
  return { svg: clean, labels: labelsOf(clean) }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type DrawState =
  | { status: 'loading' }
  | { status: 'ready'; svg: string; labels: string[] }
  | { status: 'error' }

let blockCount = 0

export const AppCoachDiagramBlock: React.FC<{ block: DiagramBlock }> = ({ block }) => {
  const { t } = useTranslation()
  const { source, title, format } = block
  const [state, setState] = useState<DrawState>({ status: 'loading' })
  // A stable id per block for aria-describedby (React 17 has no useId)
  const [descriptionId] = useState(() => {
    blockCount += 1
    return `coach-diagram-labels-${blockCount}`
  })

  useEffect(() => {
    if (format !== 'mermaid' || !isSafeMermaidSource(source)) {
      setState({ status: 'error' })
      return undefined
    }
    let cancelled = false
    setState({ status: 'loading' })
    drawDiagram(source).then(
      ({ svg, labels }) => {
        if (!cancelled) setState({ status: 'ready', svg, labels })
      },
      () => {
        if (!cancelled) setState({ status: 'error' })
      }
    )
    return () => {
      cancelled = true
    }
  }, [format, source])

  return (
    <div>
      {title && <h4 className="text-sm font-semibold text-app-grey-100 mb-2">{title}</h4>}
      {state.status === 'loading' && (
        <p role="status" className="text-xs text-app-grey-65">
          {t('askCL.diagram.loading')}
        </p>
      )}
      {state.status === 'ready' && (
        <>
          {/* role="img" hides the drawing's own text: its labels describe it instead */}
          <div
            role="img"
            aria-label={title || t('askCL.diagram.title')}
            aria-describedby={state.labels.length ? descriptionId : undefined}
            className="overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: state.svg }}
          />
          {state.labels.length > 0 && (
            <p id={descriptionId} className="sr-only">
              {state.labels.join('; ')}
            </p>
          )}
        </>
      )}
      {state.status === 'error' && (
        <div>
          <p className="text-xs text-app-grey-65 mb-1">{t('askCL.diagram.error')}</p>
          <pre className="text-xs text-app-grey-100 bg-app-grey-5 border border-app-grey-25 rounded-md p-2 whitespace-pre-wrap break-words overflow-x-auto">
            {typeof source === 'string' ? source : ''}
          </pre>
        </div>
      )}
    </div>
  )
}

export default AppCoachDiagramBlock
